import { useEffect } from 'react'
import { z } from 'zod'

import type { NavItem } from '@/shared/registry'

import { classStatuses, deliveryTypes } from '../class-form/class'
import { attendanceModes, enrollmentStatuses, type EnrollmentStatus } from '../roster/roster'

/** Where the learner's class pages live; each opens signed out when the site or the class allows it. */
export const classBrowserPath = '/classes/browse'
export const learnerClassRoute = `${classBrowserPath}/:publicId`
export const publicScheduleRoute = '/classes/public-schedule/:organizationId'

/** A class's learner page. */
export function learnerClassPath(id: string): string {
  return `${classBrowserPath}/${encodeURIComponent(id)}`
}

/** An organization's public schedule. */
export function publicSchedulePath(organizationId: string): string {
  return `/classes/public-schedule/${encodeURIComponent(organizationId)}`
}

/** The learner's pages, as `class-catalog` read gives them. */
export const readClassCatalogPermission = 'classes.class-catalog.read'

/** "Browse Classes" in the nav, while the viewer's organization has its class browser switch on. */
export const classBrowserNav: NavItem = { id: 'class-browser', labelId: 'classes.learner.browser.title', path: classBrowserPath, permission: readClassCatalogPermission }

/** The API's learner endpoints. */
export const classCatalogApi = '/classes/v1/class-catalog'

/** Whether the class browser is in the caller's menu. */
export const classBrowserVisibilityApi = `${classCatalogApi}/visibility`

/** Whether the class browser visibility answer says it shows; anything else hides it. */
export function isClassBrowserVisible(body: unknown): boolean {
  return z.object({ isVisible: z.boolean() }).safeParse(body).data?.isVisible === true
}

export function learnerClassApi(id: string): string {
  return `${classCatalogApi}/${encodeURIComponent(id)}`
}

export function publicScheduleApi(organizationId: string): string {
  return `${classCatalogApi}/public-schedules/${encodeURIComponent(organizationId)}`
}

const summarySchema = z.object({
  id: z.string(),
  name: z.string(),
  htmlDescription: z.string().nullish(),
  courseId: z.string(),
  courseName: z.string(),
  courseImageUrl: z.string().nullish(),
  organizationName: z.string(),
  status: z.enum(classStatuses),
  startsOn: z.string(),
  endsOn: z.string(),
  timeZone: z.string(),
  deliveryType: z.enum(deliveryTypes),
  allowsAnonymousDetails: z.boolean(),
})

/** A class as the learner's lists show it. */
export type LearnerClassSummary = z.infer<typeof summarySchema>

export function parseLearnerClassSummary(item: unknown): LearnerClassSummary {
  return summarySchema.parse(item)
}

const learnerClassSchema = z.object({
  id: z.string(),
  name: z.string(),
  htmlDescription: z.string().nullish(),
  courseId: z.string(),
  courseName: z.string(),
  courseImageUrl: z.string().nullish(),
  organizationId: z.string(),
  organizationName: z.string(),
  status: z.enum(classStatuses),
  timeZone: z.string(),
  deliveryLanguage: z.string().nullish(),
  deliveryType: z.enum(deliveryTypes),
  /** Whether it has an online meeting to join, through WebEx or its custom URL. */
  hasOnlineMeeting: z.boolean(),
  maximumStudents: z.number().int(),
  classrooms: z.array(z.string()),
  startsOn: z.string(),
  endsOn: z.string(),
  sessions: z.array(z.object({ id: z.string(), startsOn: z.string(), endsOn: z.string() })),
  allowsSelfEnrollment: z.boolean(),
  anyoneMaySelfEnroll: z.boolean(),
  allowsSelfEnrollmentWhileInProgress: z.boolean(),
  /** The viewer's own place on the roster, when they have one. */
  enrollment: z
    .object({ status: z.enum(enrollmentStatuses), classroomName: z.string().nullish(), attendance: z.enum(attendanceModes) })
    .nullish(),
})

/** A class as its learner page shows it. */
export type LearnerClass = z.infer<typeof learnerClassSchema>

export function parseLearnerClass(body: unknown): LearnerClass {
  return learnerClassSchema.parse(body)
}

const myPlaceChangedEvent = 'classes:my-place-changed'

/** The viewer's own place in a class as Entitlements reads it after changing it: its status, or `null` for none. */
export interface MyPlaceChange {
  readonly classId: string
  readonly status: EnrollmentStatus | null
}

/** Tells the learner class page that the viewer changed their own place in the class, such as by enrolling or canceling. */
export function announceMyPlaceChanged(change: MyPlaceChange): void {
  window.dispatchEvent(new CustomEvent<MyPlaceChange>(myPlaceChangedEvent, { detail: change }))
}

/** Calls `onChange` with the new status whenever the viewer's own place in the class is changed from this page. */
export function useMyPlaceChanged(classId: string, onChange: (status: EnrollmentStatus | null) => void): void {
  useEffect(() => {
    const listener = (event: Event) => {
      const change = event instanceof CustomEvent ? (event.detail as MyPlaceChange) : null
      if (change?.classId === classId) {
        onChange(change.status)
      }
    }
    window.addEventListener(myPlaceChangedEvent, listener)
    return () => window.removeEventListener(myPlaceChangedEvent, listener)
  }, [classId, onChange])
}
